/**
 * Kunden-Phase Helper — Issue #424
 *
 * Bildet die internen 6 Hauptphasen + Sub-Stati auf 6 kundenfreundliche
 * Phasen ab. Pure Funktion, ohne Supabase-Zugriff. Wird sowohl im Frontend
 * (KundenLinkPortal) als auch im Backend (Progress-API) verwendet.
 *
 * Wichtig: Niemals interne Begriffe wie „QS", „Kürzung", „Klage",
 * „Stellungnahme" oder „Mahnbescheid" an den Kunden kommunizieren — die
 * sind nur intern und würden den Kunden verunsichern.
 */

export type Hauptphase = 1 | 2 | 3 | 4 | 5 | 6;
export type KundenPhase = 1 | 2 | 3 | 4 | 5 | 6;
export type ZahlungStatus = 'offen' | 'teilweise' | 'bezahlt';

export interface AkteProgressInput {
  hauptphase: Hauptphase;
  /** Hauptphase 3: Gutachten ist final QS-freigegeben */
  qs_freigegeben?: boolean;
  /** Hauptphase 3: Gutachten geht zurück in Korrektur */
  qs_korrektur?: boolean;
  /** Zahlung der Versicherung */
  zahlung_status?: ZahlungStatus | null;
  /** Hauptphase 5: Stellungnahme erforderlich oder versendet */
  stellungnahme_aktiv?: boolean;
  /** Hauptphase 5: Klage / Mahnbescheid läuft */
  klage_aktiv?: boolean;
}

export const KUNDEN_PHASE_LABELS: Record<KundenPhase, string> = {
  1: 'Auftrag eingegangen',
  2: 'Besichtigung',
  3: 'Gutachten erstellt',
  4: 'Versand an Versicherung',
  5: 'Regulierung läuft',
  6: 'Abgeschlossen',
};

export const KUNDEN_PHASE_TEXTS: Record<KundenPhase, string> = {
  1: 'Ihr Auftrag ist bei uns eingegangen.',
  2: 'Wir haben Ihr Fahrzeug besichtigt.',
  3: 'Das Gutachten ist fertig.',
  4: 'Wir haben das Gutachten an die Versicherung geschickt.',
  5: 'Wir klären gerade die Abrechnung mit der Versicherung.',
  6: 'Ihr Fall ist abgeschlossen.',
};

export function kundenPhaseLabel(phase: KundenPhase): string {
  return KUNDEN_PHASE_LABELS[phase];
}

export function kundenPhaseText(phase: KundenPhase): string {
  return KUNDEN_PHASE_TEXTS[phase];
}

/**
 * Mapping-Regeln (siehe Issue #424):
 * - Hauptphase 1 → 1
 * - Hauptphase 2 → 2
 * - Hauptphase 3 + qs_freigegeben → 3, sonst → 2 (Gutachten noch nicht
 *   final, Kunde sieht weiter „Besichtigung")
 * - Hauptphase 4 → 4
 * - Hauptphase 5 → 5 (Klage, Stellungnahme, Teil-Zahlung — alles
 *   zusammengefasst als „Regulierung läuft")
 * - Hauptphase 6 + zahlung=bezahlt → 6, sonst → 5 (intern abgeschlossen,
 *   aber für den Kunden ist die Regulierung weiter aktiv bis bezahlt)
 */
export function bestimmeKundenPhase(input: AkteProgressInput): KundenPhase {
  const { hauptphase } = input;

  switch (hauptphase) {
    case 1:
      return 1;
    case 2:
      return 2;
    case 3:
      return input.qs_freigegeben && !input.qs_korrektur ? 3 : 2;
    case 4:
      return 4;
    case 5:
      return 5;
    case 6:
      return input.zahlung_status === 'bezahlt' ? 6 : 5;
    default:
      return 1;
  }
}

/**
 * Felder aus der `akten`-Tabelle, die für die Phasen-Ableitung relevant sind.
 * Bewusst lose typisiert (alle optional), damit die Ableitung sowohl mit dem
 * heutigen Schema als auch mit dem zukünftigen `hauptphase`-Feld (Issue #412)
 * funktioniert.
 */
export interface AkteFelder {
  status?: string | null;
  hauptphase?: number | null;
  besichtigungsdatum?: string | null;
  rechnung_nr?: string | null;
  rechnung_datum?: string | null;
  rechnung_betrag?: number | null;
  rechnung_bezahlt?: boolean | null;
  rechnung_bezahlt_betrag?: number | null;
  kuerzung_betrag?: number | null;
  stellungnahme_status?: string | null;
  pruefung_status?: string | null;
}

/**
 * Best-effort-Ableitung der Hauptphase + Sub-Stati aus den heute existierenden
 * Akten-Feldern. Sobald Issue #412 das `hauptphase`-Feld liefert, wird das
 * direkt übernommen; bis dahin werden die Phasen aus Indikator-Feldern
 * abgeleitet.
 */
export function ableitungAusAkte(akte: AkteFelder): AkteProgressInput {
  if (typeof akte.hauptphase === 'number' && akte.hauptphase >= 1 && akte.hauptphase <= 6) {
    return {
      hauptphase: akte.hauptphase as Hauptphase,
      zahlung_status: leiteZahlungStatusAb(akte),
      stellungnahme_aktiv: istStellungnahmeAktiv(akte),
      qs_freigegeben: akte.pruefung_status === 'qs_freigegeben',
      qs_korrektur:   akte.pruefung_status === 'qs_korrektur',
    };
  }

  const zahlung_status = leiteZahlungStatusAb(akte);
  const stellungnahme_aktiv = istStellungnahmeAktiv(akte);

  if (akte.status === 'abgeschlossen') {
    return { hauptphase: 6, zahlung_status };
  }

  const istRegulierung =
    stellungnahme_aktiv ||
    zahlung_status === 'teilweise' ||
    (akte.kuerzung_betrag ?? 0) > 0;

  if (istRegulierung) {
    return { hauptphase: 5, zahlung_status, stellungnahme_aktiv };
  }

  if (akte.rechnung_datum) {
    return { hauptphase: 4 };
  }

  if (akte.rechnung_nr) {
    return { hauptphase: 3, qs_freigegeben: true };
  }

  if (akte.besichtigungsdatum) {
    return { hauptphase: 2 };
  }

  return { hauptphase: 1 };
}

/**
 * Whitelisted Response des Progress-API. Das ist die EINZIGE Form, in der
 * Kunden-Daten das Backend verlassen — keine internen Felder, keine
 * Sub-Stati-Bezeichner.
 */
export interface KundenProgressResponse {
  kunden_phase: KundenPhase;
  kunden_phase_label: string;
  kunden_phase_text: string;
  letzte_aktualisierung: string | null;
  warten_auf_kunde: string | null;
  kunden_nachricht: string | null;
}

export const KUNDEN_PROGRESS_RESPONSE_KEYS: ReadonlyArray<keyof KundenProgressResponse> = [
  'kunden_phase',
  'kunden_phase_label',
  'kunden_phase_text',
  'letzte_aktualisierung',
  'warten_auf_kunde',
  'kunden_nachricht',
];

const AKTION_KUNDENLABELS: Record<string, string> = {
  dokument_hochladen: 'Dokumente',
  unterschrift_auftrag: 'Unterschrift Auftrag',
  unterschrift_abtretung: 'Unterschrift Abtretung',
  unterschrift_mietwagen: 'Unterschrift Mietwagenvertrag',
  unterschrift_vollmacht: 'Unterschrift Vollmacht',
  unterschrift_anwaltsvollmacht: 'Unterschrift Anwaltsvollmacht',
};

/**
 * Baut die kundengerichtete Progress-Antwort aus Akten- und Link-Daten.
 *
 * Garantiert, dass NUR whitelisted Felder im Output landen. Auch wenn der
 * Caller mehr Felder mitgibt, werden sie nicht durchgereicht.
 */
export function buildKundenProgressResponse(
  akte:
    | (AkteFelder & {
        aktualisiert_am?: string | null;
        updated_at?: string | null;
        kunden_nachricht?: string | null;
      })
    | null,
  link: {
    aktionen?: string[] | null;
    erledigte_aktionen?: string[] | null;
  } | null,
): KundenProgressResponse {
  const input = akte ? ableitungAusAkte(akte) : { hauptphase: 1 as Hauptphase };
  const phase = bestimmeKundenPhase(input);

  const aktionen = link?.aktionen ?? [];
  const erledigt = new Set(link?.erledigte_aktionen ?? []);
  const offen = aktionen.filter((a) => !erledigt.has(a));
  const warten_auf_kunde =
    offen.length > 0 ? AKTION_KUNDENLABELS[offen[0]] ?? offen[0] : null;

  return {
    kunden_phase: phase,
    kunden_phase_label: kundenPhaseLabel(phase),
    kunden_phase_text: kundenPhaseText(phase),
    letzte_aktualisierung: akte?.aktualisiert_am ?? akte?.updated_at ?? null,
    warten_auf_kunde,
    kunden_nachricht: akte?.kunden_nachricht ?? null,
  };
}

function leiteZahlungStatusAb(akte: AkteFelder): ZahlungStatus | null {
  if (akte.rechnung_bezahlt) return 'bezahlt';
  const betrag = akte.rechnung_betrag ?? 0;
  const bezahlt = akte.rechnung_bezahlt_betrag ?? 0;
  if (bezahlt > 0 && betrag > 0 && bezahlt < betrag) return 'teilweise';
  if (akte.rechnung_datum) return 'offen';
  return null;
}

function istStellungnahmeAktiv(akte: AkteFelder): boolean {
  return akte.stellungnahme_status === 'offen' || akte.stellungnahme_status === 'versendet';
}
